// Review-capability redaction for telemetry egress (approvals B2, design D9).
//
// A client review link `/review/<token>` IS the credential: whoever holds the
// 48-char hex token can act on the review. So no copy of it may leave the
// browser in telemetry. This module is the single redaction function every
// Sentry egress hook calls (see src/lib/sentry.ts). It is deliberately pure —
// no Sentry import, no mutation of its input — so it can be unit-tested and
// reused by any other telemetry sink.
//
// It deep-walks a JSON-like value and returns a redacted copy:
//   - `/review/<segment>`      → `/review/[redacted]` (segment ends at
//     `/ ? #`, whitespace, a quote, `<` or `>`). Fail-closed: this also
//     redacts `/command/review/<preparedActionId>` — an accepted cost.
//   - `%2Freview%2F<segment>`  → `%2Freview%2F[redacted]` (URL-encoded form,
//     case-insensitive on the escapes).
//   - a standalone 48-hex run  → `[redacted-token]`. "Standalone" means not
//     adjacent to another hex character, so 64-hex content hashes are left
//     alone while a token glued to non-hex text (`x_<token>`) is still caught.
//   - the value of any key named `token`, `reviewUrl`, `review_url` or
//     `review_token` (case-insensitive) → `[redacted]`, whatever its type.
//   - object keys are redacted with the same string rules.
//
// Fail-closed limits: a cycle becomes REDACTED_CYCLE and anything nested
// deeper than the depth cap becomes REDACTED_DEPTH — the raw value is never
// passed through un-walked.
//
// Non-plain objects (opaque): by default (`opaque: "redact"`) they never pass
// through raw, because breadcrumbs reach `beforeBreadcrumb` un-normalized
// (core breadcrumbs.js passes the raw merged crumb) and are JSON-serialized
// later (`URL.toJSON()` is its href):
//   - Error → plain `{...own enumerable fields, name, message, stack, cause}`
//     (keeps PostgREST `code`/`details`/`hint`), walked;
//   - anything with `toJSON()` (URL, Date) → the toJSON result, walked;
//   - a class instance / cross-realm object (`[object Object]` tag) → its own
//     enumerable fields, walked;
//   - anything else (Map, Response, DOM node) → `String(value)`, redacted;
//   - if any of that throws → REDACTED_OPAQUE.
// `opaque: "preserve"` returns non-plain objects by identity. It exists only
// for `beforeStartSpan`, whose options carry SDK Scope/Span instances that
// the SDK needs back unchanged.

/** Default depth cap. Sentry normalizes event payloads to depth 3, so 20 is ample. */
export const REDACTION_MAX_DEPTH = 20;
/** Marker for values nested beyond the depth cap. */
export const REDACTED_DEPTH = "[redacted-depth]";
/** Marker for a reference back to an ancestor (a cycle). */
export const REDACTED_CYCLE = "[redacted-cycle]";
/** Marker for the value of a token-named key. */
export const REDACTED_VALUE = "[redacted]";
/** Marker for a non-plain object that could not be serialized safely. */
export const REDACTED_OPAQUE = "[redacted-opaque]";

const PATH_RE = /\/review\/[^/?#\s"'<>]+/gi;
const ENCODED_PATH_RE = /%2Freview%2F[^/?#\s"'<>%&]+/gi;
// No lookbehind: older Safari cannot parse it, and this module loads eagerly.
const HEX_TOKEN_RE = /(^|[^0-9a-f])[0-9a-f]{48}(?![0-9a-f])/gi;

const TOKEN_KEYS = new Set([
  "token",
  "reviewurl",
  "review_url",
  "review_token",
]);

/** Options for {@link redactCapabilities}. */
export interface RedactOptions {
  /** Maximum nesting depth walked before failing closed. Default {@link REDACTION_MAX_DEPTH}. */
  maxDepth?: number;
  /**
   * How to treat non-plain objects. `"redact"` (default) serializes and
   * redacts them; `"preserve"` returns them by identity (beforeStartSpan only).
   */
  opaque?: "redact" | "preserve";
}

/** Internal walk state shared down the recursion. */
interface WalkState {
  maxDepth: number;
  opaque: "redact" | "preserve";
  ancestors: Set<object>;
}

/** Redacts review capabilities from one string. */
function redactString(s: string): string {
  return s
    .replace(PATH_RE, "/review/[redacted]")
    .replace(ENCODED_PATH_RE, (m) => `${m.slice(0, 12)}[redacted]`)
    .replace(HEX_TOKEN_RE, "$1[redacted-token]");
}

/** True for `{}`-literal / `Object.create(null)` objects — the only ones cloned field by field. */
function isPlainObject(v: object): v is Record<string, unknown> {
  const proto = Object.getPrototypeOf(v) as unknown;
  return proto === Object.prototype || proto === null;
}

/** Own enumerable fields of an Error plus its standard fields and `cause`. */
function errorSource(e: Error): Record<string, unknown> {
  const src: Record<string, unknown> = { ...e };
  src.name = e.name;
  src.message = e.message;
  src.stack = e.stack;
  if ("cause" in e) src.cause = (e as { cause?: unknown }).cause;
  return src;
}

/**
 * Turns a non-plain object into something the walker can redact (see the
 * file header). Returns `{ walk }` to recurse into, or `{ value }` final.
 */
function openOpaque(v: object): { walk: unknown } | { value: unknown } {
  try {
    if (v instanceof Error) return { walk: errorSource(v) };
    const toJSON = (v as { toJSON?: unknown }).toJSON;
    if (typeof toJSON === "function") {
      return { walk: (toJSON as () => unknown).call(v) };
    }
    if (Object.prototype.toString.call(v) === "[object Object]") {
      return { walk: { ...v } };
    }
    return { value: redactString(String(v)) };
  } catch {
    return { value: REDACTED_OPAQUE };
  }
}

/** Recursive worker. `state.ancestors` holds the objects on the current path (cycle guard). */
function walk(v: unknown, depth: number, state: WalkState): unknown {
  if (typeof v === "string") return redactString(v);
  if (v === null || typeof v !== "object") return v;
  if (state.ancestors.has(v)) return REDACTED_CYCLE;
  if (depth >= state.maxDepth) return REDACTED_DEPTH;

  if (!Array.isArray(v) && !isPlainObject(v)) {
    if (state.opaque === "preserve") return v;
    const opened = openOpaque(v);
    if ("value" in opened) return opened.value;
    state.ancestors.add(v);
    try {
      return walk(opened.walk, depth + 1, state);
    } finally {
      state.ancestors.delete(v);
    }
  }

  state.ancestors.add(v);
  try {
    if (Array.isArray(v)) {
      return v.map((item) => walk(item, depth + 1, state));
    }
    const out: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(v)) {
      out[redactString(key)] = TOKEN_KEYS.has(key.toLowerCase())
        ? REDACTED_VALUE
        : walk(value, depth + 1, state);
    }
    return out;
  } finally {
    state.ancestors.delete(v);
  }
}

/**
 * Returns a copy of `value` with every review capability redacted (see the
 * file header for the exact rules). Never mutates `value`; never throws on
 * cycles; fails closed past `options.maxDepth` and on opaque objects unless
 * `options.opaque === "preserve"`. It CAN throw if a getter on the input
 * throws — every Sentry hook wraps it and fails closed (see sentry.ts).
 */
export function redactCapabilities<T>(
  value: T,
  options: RedactOptions = {},
): T {
  return walk(value, 0, {
    maxDepth: options.maxDepth ?? REDACTION_MAX_DEPTH,
    opaque: options.opaque ?? "redact",
    ancestors: new Set(),
  }) as T;
}
